'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Columns3 } from 'lucide-react';

import { parseHiddenColumns, toggleHiddenColumn, type DeskListKind } from '@/lib/desk/list-state';

const SKELETON_ROWS = 6;

export function DeskTable({
  storageKey,
  columns,
  filters,
  search,
  selectedCount,
  allVisibleSelected,
  onToggleVisible,
  batch,
  onClearSelection,
  listState,
  refreshing = false,
  refreshError = false,
  sort,
  onSort,
  onRetry,
  emptyTitle,
  filteredTitle,
  errorTitle,
  notice,
  noticeTone = 'ok',
  onDismissNotice,
  children,
}: {
  storageKey: string;
  columns: { id: string; label: string; sortable?: boolean }[];
  filters?: ReactNode;
  search?: ReactNode;
  selectedCount: number;
  allVisibleSelected: boolean;
  onToggleVisible: () => void;
  batch?: ReactNode;
  onClearSelection: () => void;
  listState: DeskListKind;
  refreshing?: boolean;
  refreshError?: boolean;
  sort?: { id: string; direction: 'asc' | 'desc' };
  onSort?: (columnId: string) => void;
  onRetry?: () => void;
  emptyTitle: string;
  filteredTitle: string;
  errorTitle: string;
  notice: string | null;
  noticeTone?: 'ok' | 'error';
  onDismissNotice: () => void;
  children: (shown: (columnId: string) => boolean, columnCount: number) => ReactNode;
}) {
  const [hidden, setHidden] = useState<string[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnKey = columns.map((column) => column.id).join('|');
  const dismissNotice = useRef(onDismissNotice);
  dismissNotice.current = onDismissNotice;

  useEffect(() => {
    setHidden(parseHiddenColumns(window.localStorage.getItem(storageKey), columnKey.split('|')));
  }, [storageKey, columnKey]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => dismissNotice.current(), 3200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  function toggleColumn(columnId: string) {
    setHidden((current) => {
      const next = toggleHiddenColumn(current, columnId);
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }

  const shown = (columnId: string) => !hidden.includes(columnId);
  const visibleColumns = columns.filter((column) => shown(column.id));
  const columnCount = visibleColumns.length + 2;

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-card">
      <div className="flex flex-col gap-2 border-b border-line px-3 py-3 lg:flex-row lg:items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {search}
          {filters}
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            aria-expanded={columnsOpen}
            onClick={() => setColumnsOpen((open) => !open)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs font-semibold text-ink"
          >
            <Columns3 size={14} /> Columns
          </button>
          {columnsOpen && (
            <div className="absolute right-0 z-20 mt-1 w-44 rounded-xl border border-line bg-card p-2 shadow-xl">
              {columns.map((column) => (
                <label key={column.id} className="flex items-center gap-2 px-2 py-1.5 text-xs text-ink">
                  <input type="checkbox" checked={shown(column.id)} onChange={() => toggleColumn(column.id)} />
                  {column.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-brand/10 px-3 py-2 text-xs text-ink">
          <span className="font-semibold">{selectedCount} selected</span>
          {batch}
          <button type="button" onClick={onClearSelection} className="rounded-full border border-line px-3 py-1 font-semibold">
            Clear
          </button>
        </div>
      )}
      {refreshing && (
        <div className="h-0.5 animate-pulse bg-brand" role="status" aria-label="Refreshing" />
      )}
      {refreshError && (
        <div className="flex items-center justify-between gap-2 border-b border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
          <p>The latest refresh failed. The rows below are the last successful result.</p>
          {onRetry && (
            <button type="button" onClick={onRetry} className="rounded-full border border-line px-3 py-1 font-semibold text-ink">
              Retry
            </button>
          )}
        </div>
      )}
      {notice && (
        <p className={`border-b px-3 py-2 text-xs font-semibold ${noticeTone === 'error' ? 'border-danger/30 bg-danger/10 text-danger' : 'border-success/30 bg-success/10 text-success'}`}>
          {notice}
        </p>
      )}
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead className="type-table-header sticky top-0 z-10 bg-shell text-muted">
            <tr>
              <th className="px-3 py-2">
                <input type="checkbox" checked={allVisibleSelected} onChange={onToggleVisible} aria-label="Select visible rows" />
              </th>
              {visibleColumns.map((column) => (
                <th key={column.id} className="px-3 py-2" aria-sort={sort?.id === column.id ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  {column.sortable && onSort ? (
                    <button type="button" onClick={() => onSort(column.id)} className="inline-flex items-center gap-1 font-semibold">
                      {column.label}
                      {sort?.id === column.id ? (sort.direction === 'asc' ? '↑' : '↓') : ''}
                    </button>
                  ) : column.label}
                </th>
              ))}
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="type-table-cell text-ink">
            {listState === 'loading' && Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <tr key={index} className="border-t border-line">
                {Array.from({ length: columnCount }, (_, cell) => (
                  <td key={cell} className="px-3 py-3">
                    <div className="h-4 animate-pulse rounded bg-shell" />
                  </td>
                ))}
              </tr>
            ))}
            {listState === 'error' && (
              <tr>
                <td colSpan={columnCount} className="px-3 py-10 text-center text-sm text-danger">
                  <p>{errorTitle}</p>
                  {onRetry && (
                    <button type="button" onClick={onRetry} className="mt-3 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
                      Retry
                    </button>
                  )}
                </td>
              </tr>
            )}
            {listState === 'empty' && (
              <tr><td colSpan={columnCount} className="px-3 py-10 text-center text-sm text-muted">{emptyTitle}</td></tr>
            )}
            {listState === 'filtered' && (
              <tr><td colSpan={columnCount} className="px-3 py-10 text-center text-sm text-muted">{filteredTitle}</td></tr>
            )}
            {listState === 'ready' && children(shown, columnCount)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
